import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core';
import { DxScrollViewModule, DxValidatorModule } from 'devextreme-angular';
import { DxSortableComponent } from 'devextreme-angular/ui/sortable';
import { DxValidatorComponent } from 'devextreme-angular/ui/validator';
import { DxiValidationRuleComponent } from 'devextreme-angular/ui/nested';
import { GpdButtonComponent } from '../../../../../../../components/gpd-button/gpd-button.component';
import { SvgIconComponent } from '../../../../../../../components/svg-icon/svg-icon.component';
import { SetupMilestoneNodeComponent } from '../../node-types/setup-milestone-node/setup-milestone-node.component';
import { MultiStepFormComponent } from '../../../../../../../components/multi-step-form/multi-step-form.component';
import { AddEvent } from 'devextreme/ui/sortable';
import { Milestone, Phase } from '../../../../../../../interfaces/entities/project-configuration';

@Component({
    selector: 'app-setup-milestones-step',
    imports: [
        CommonModule,
        SetupMilestoneNodeComponent,
        DxSortableComponent,
        DxScrollViewModule,
        DxValidatorComponent,
        DxiValidationRuleComponent,
        DxValidatorModule,
        GpdButtonComponent,
        SvgIconComponent,
    ],
    templateUrl: './setup-milestones-step.component.html',
    styleUrl: './setup-milestones-step.component.css',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SetupMilestonesStepComponent {
  @Input() phases: Phase[] = [];
  @Input() validationGroup: any;
  @Input() multiStepForm?: MultiStepFormComponent;

  @Output() phasesChange = new EventEmitter<Phase[]>();
  @Output() validationUpdate = new EventEmitter<void>();

  // Callbacks
  milestoneValidationCallbacks: Array<() => void> = [];

  constructor(private cdr: ChangeDetectorRef) {}

  milestoneAdapterConfig = {
    getValue: () => {
      return this.milestoneValidationCallback();
    },
    validationRequestsCallbacks: this.milestoneValidationCallbacks,
  };

  milestoneValidationCallback = () => {
    // Collect all milestones from all phases
    const allMilestones: Milestone[] = [];
    for (const phase of this.phases) {
      if (phase.milestones?.length > 0) {
        allMilestones.push(...phase.milestones);
      }
    }

    if (allMilestones.length === 0) {
      return false;
    }

    // Check each milestone for validity (mimic phase validation pattern)
    for (const milestone of allMilestones) {
      // Check if milestone has a name
      if (!milestone.name || milestone.name.trim() === '') {
        return false;
      }

      // Check weighting range (0-100)
      if (milestone.weight < 0 || milestone.weight > 100) {
        return false;
      }

      // Check for duplicate names (case-insensitive) - exact same pattern as phase validation
      const duplicateExists = allMilestones.some(otherMilestone => {
        return (
          otherMilestone !== milestone &&
          otherMilestone.name &&
          otherMilestone.name.trim().toLowerCase() === milestone.name.trim().toLowerCase()
        );
      });

      if (duplicateExists) {
        return false;
      }
    }

    return true;
  };

  // DevExtreme Sortable handles cross-component drag operations automatically
  onAdd(event?: AddEvent) {
    // DevExtreme handles the data manipulation automatically
    // Just emit the updated phases and trigger validation
    this.phasesChange.emit([...this.phases]);
    this.cdr.detectChanges();
    this.multiStepForm?.triggerValidationUpdate();
  }

  onMilestoneAdd(phase: Phase, event?: any) {
    const { fromIndex, toIndex, fromData, toData } = event;
    console.log(event);

    var data = fromData[fromIndex];
    fromData.splice(fromIndex, 1);
    console.log(data);
    console.log(toData);

    toData.splice(toIndex, 0, data);
    this.phasesChange.emit([...this.phases]);
    this.cdr.detectChanges();
    this.multiStepForm?.triggerValidationUpdate();
  }

  onMilestoneCreate(phase: Phase) {
    const allMilestones = this.phases.flatMap(p => p.milestones || []);
    const tempId = Math.min(...allMilestones.map(m => m.id || 0), 0) - 1;

    const newMilestone: Milestone = {
      id: tempId,
      name: '',
      weight: 0,
    };

    if (!phase.milestones) {
      phase.milestones = [];
    }

    phase.milestones.push(newMilestone);
    this.phasesChange.emit(this.phases);
    this.cdr.detectChanges();

    this.multiStepForm?.triggerValidationUpdate();
    // Force validation group to recognize new components
    if (this.validationGroup) {
      this.multiStepForm?.resetValidation();
    }
  }

  onMilestoneDelete = (phase: Phase, milestone: Milestone) => {
    if (phase.milestones) {
      const index = phase.milestones.indexOf(milestone);
      if (index > -1) {
        phase.milestones.splice(index, 1);
        this.phasesChange.emit(this.phases);
        this.cdr.detectChanges();

        // Trigger validation update after DOM updates
        this.multiStepForm?.triggerValidationUpdate();
      }
    }
    this.milestoneValidationCallbacks.forEach(callback => callback());
  };

  onMilestoneReorder = (phase: Phase, event: any) => {
    const { fromIndex, toIndex, fromData, toData } = event;

    if (fromIndex !== toIndex && phase.milestones && !event.dropInsideItem) {
      var data = fromData[fromIndex];
      fromData.splice(fromIndex, 1);
      toData.splice(toIndex, 0, data);
      this.phasesChange.emit([...this.phases]);
      this.cdr.detectChanges();

      // Trigger validation update after reordering
      this.multiStepForm?.triggerValidationUpdate();
    }
  };

  togglePhaseExpansion = (phase: Phase) => {
    // Add expanded property to phase if it doesn't exist
    if (!('expanded' in phase)) {
      (phase as any).expanded = true;
    }

    (phase as any).expanded = !(phase as any).expanded;
    this.cdr.detectChanges();
  };

  isPhaseExpanded = (phase: Phase): boolean => {
    return (phase as any).expanded !== false; // Default to true if not set
  };

  hasValidMilestones = (phase: Phase): boolean => {
    return phase.milestones && phase.milestones.length > 0;
  };

  onMilestoneNameChange = (milestone: Milestone, event: any) => {
    milestone.name = event.value;
    this.phasesChange.emit(this.phases);
    this.cdr.detectChanges();

    // Trigger validation update
    this.multiStepForm?.triggerValidationUpdate();
  };

  onMilestoneWeightingChange = (milestone: Milestone, event: any) => {
    milestone.weight = event.value;
    this.phasesChange.emit(this.phases);
    this.cdr.detectChanges();

    // Trigger validation update
    this.multiStepForm?.triggerValidationUpdate();
  };
}
